import { useEffect, useState, useCallback } from "react";
import { api, ACCOUNT_COLORS } from "@/lib/api";

function relTime(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function ActivityLog({ leagueId, limit = 20 }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    if (!leagueId) return;
    api.getActivity(leagueId, limit).then((d) => setItems(d.items || [])).catch(() => {});
  }, [leagueId, limit]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)]" data-testid="activity-log">
      <div className="p-4 md:p-5 border-b border-[var(--stakes-border)] flex items-center justify-between">
        <div>
          <div className="label-mono">RECENT ACTIVITY</div>
          <h3 className="font-anton text-2xl uppercase leading-none mt-1 tracking-tight">THE LEDGER</h3>
        </div>
        <div className="label-mono">{items.length} ENTRIES</div>
      </div>

      {items.length === 0 ? (
        <div className="p-6 label-mono text-center">NO ACTIVITY YET</div>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-[var(--stakes-border)]">
          {items.map((it) => {
            const color = ACCOUNT_COLORS[it.user_role] || "#fff";
            const positive = it.points >= 0;
            // For redemption claims, output exactly: "{USERNAME} COMPLETED REDEMPTION TASK"
            // (avoids the legacy "Redemption Redemption Task" duplication).
            const isRedemption = !!it.is_redemption;
            const isBonus = !!it.is_bonus;
            const verb = isBonus
              ? "earned"
              : isRedemption
              ? "completed redemption task"
              : it.value === "yes"
              ? "checked"
              : it.is_auto
              ? "auto-failed"
              : "missed";
            return (
              <div
                key={it.id}
                className="flex items-center gap-3 px-4 py-3"
                data-testid={`activity-row-${it.id}`}
              >
                <div
                  className="w-9 h-9 flex-shrink-0 flex items-center justify-center font-anton text-lg"
                  style={{ background: isBonus ? "transparent" : color, color: isBonus ? "var(--stakes-child)" : "#000", border: isBonus ? "1px solid var(--stakes-child)" : "none" }}
                >
                  {isBonus ? "★" : it.user_name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-barlow text-base uppercase wrap-words break-words whitespace-normal">
                    <span className="font-bold">{it.user_name}</span>{" "}
                    <span className={isRedemption || isBonus ? "font-bold" : "text-[var(--stakes-muted)]"}>{verb}</span>
                    {!isRedemption && (
                      <>
                        {" "}
                        <span className="font-bold">{it.rule_text || "—"}</span>
                      </>
                    )}
                    {it.has_photo && <span className="ml-1" title="Photo proof">📸</span>}
                    {it.is_auto && <span className="label-mono ml-2" style={{ color: "var(--stakes-primary)" }}>AUTO</span>}
                  </div>
                  <div className="label-mono mt-0.5">{it.date} • {relTime(it.updated_at)} ago</div>
                </div>
                <div
                  className="font-anton text-xl leading-none"
                  style={{ color: positive ? "var(--stakes-child)" : "var(--stakes-primary)" }}
                >
                  {positive ? "+" : ""}{it.points} pts
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
